import React, { useCallback } from "react"
import { useTranslation } from "react-i18next"

import classNames from "classnames"

import { getUserColor } from "shared/lib/user/getUserColor"
import { SelectOption } from "shared/types/Select"
import { WithClassName } from "shared/types/WithClassName"
import { TeamMember } from "shared/types/__generated__/graphql"
import { Combobox } from "shared/ui/input/combobox/Combobox"
import { TextAvatar } from "shared/ui/text-avatar/TextAvatar"

import { TeamRole } from "entities/team-members/types/TeamRole"
import { ParseKeys } from "i18next"

import "./styles/_team-member-input.scss"

const TEAM_ROLE_TRANSLATION_KEYS: Record<TeamRole, string> = {
  [TeamRole.ADMIN]: "Team.Role.admin",
  [TeamRole.WRITE]: "Team.Role.write",
  [TeamRole.READ]: "Team.Role.read"
}

interface TeamMemberOptionProps {
  value: SelectOption<string, TeamMember> | null
}

const TeamMemberOption = React.memo(({ value }: TeamMemberOptionProps) => {
  const { t } = useTranslation()

  if (!value?.data) {
    return value ? (
      <span>{value.label}</span>
    ) : (
      <span className="combobox__placeholder">{t("Team.SelectAdmin")}</span>
    )
  }

  return (
    <div className="team-member-option">
      <div className="team-member-option__user-name">
        <TextAvatar
          initials={value.data.user?.name?.at(0)?.toUpperCase() ?? ""}
          className="text-avatar--small"
          color={getUserColor(value.data.user?.id)}
        />
        <span className="team-member-option__label">{value.label}</span>
      </div>

      <span className="team-member-option__role">
        {
          t(
            (TEAM_ROLE_TRANSLATION_KEYS[value.data.role as TeamRole] ??
              "Team.Role.unknown") as ParseKeys
          ) as string
        }
      </span>
    </div>
  )
})

TeamMemberOption.displayName = "TeamMemberOption"

export interface TeamMemberInputProps extends WithClassName {
  options: SelectOption<string, TeamMember>[] | undefined
  name?: string
  loading?: boolean
  disabled?: boolean
}

export const TeamMemberInput = React.memo(
  ({
    options,
    loading,
    disabled,
    name = "memberID",
    className
  }: TeamMemberInputProps) => {
    const { t } = useTranslation()

    const renderValue = useCallback(
      (value: SelectOption<string, TeamMember> | null) => {
        if (value === null) {
          return (
            <span className="combobox__placeholder">
              {t("Team.SelectAdmin")}
            </span>
          )
        }

        return <span className="mx-1">{value.label}</span>
      },
      []
    )

    return (
      <Combobox.Root
        name={name}
        inputID="team-member-input"
        data-testid="team-member-input"
        label={t("Team.SelectNewAdmin")}
        className={classNames("team-member-input", className)}
        loading={loading}
        disabled={disabled}
        items={options || undefined}
      >
        <Combobox.Label />
        <Combobox.Control>
          <Combobox.Trigger>
            <Combobox.Value>{renderValue}</Combobox.Value>
            <Combobox.Icon />
          </Combobox.Trigger>
          <Combobox.Portal>
            <Combobox.Positioner align="start" sideOffset={8}>
              <Combobox.Popup aria-label={t("Team.SelectNewAdmin")}>
                <Combobox.List>
                  {(option: SelectOption<string, TeamMember>) => (
                    <Combobox.Item key={option.value} value={option}>
                      <Combobox.ItemIndicator />
                      <Combobox.ItemContent>
                        <TeamMemberOption value={option} />
                      </Combobox.ItemContent>
                    </Combobox.Item>
                  )}
                </Combobox.List>
              </Combobox.Popup>
            </Combobox.Positioner>
          </Combobox.Portal>
        </Combobox.Control>
        <Combobox.ErrorMessage />
      </Combobox.Root>
    )
  }
)
